import {Async, AsyncPage, Empty} from 'Core/models/Async';
import {
  EnrollmentFilesNotProcessed,
  PendingEnrollmentFileStatus,
  EnrollmentFileStatus,
} from 'Enrollment/models/EnrollmentFilesNotProcessed';
import {createAction} from 'Shared/utilities/actionCreator';
import {Page} from 'Core/models/Page';
import {pipe} from 'fp-ts/lib/pipeable';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {constant} from 'fp-ts/lib/function';
import {PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {FilterOptionUnion, FilterOptionValue} from './../../Core/models/FilterOption';
import {DateRange, EmptyDateRange} from 'Core/models/DateRange';
import {InequalityRangeFilter} from 'Core/models/InequalityRange';
import * as O from 'fp-ts/lib/Option';

export enum EnrollmentFilesNotProcessedActionType {
  GET_ENROLLMENT_FILES_NOT_PROCESSED = 'enrollmentFile/GET_ENROLLMENT_FILES_NOT_PROCESSED',
  CHANGE_ENROLLMENT_FILES_NOT_PROCESSED = 'enrollmentFile/CHANGE_ENROLLMENT_FILES_NOT_PROCESSED',
  CHANGE_ENROLLMENT_FILES_NOT_PROCESSED_FILTER = 'enrollmentFile/CHANGE_ENROLLMENT_FILES_NOT_PROCESSED_FILTER',
  GET_ENROLLMENT_FILE_STATUS = 'enrollmentFile/GET_ENROLLMENT_FILE_STATUS',
  CHANGE_ENROLLMENT_FILE_STATUS = 'enrollmentFile/CHANGE_ENROLLMENT_FILE_STATUS',
}

export type FilterEnum = {
  name: string;
  value: string;
};

export const PendingEnroll: PendingEnrollmentFileStatus = {
  fileName: '',
  notificationDate: new Date(),
};

export const EnrollmentFileRejectFilters: EnrollmentFilesNotProcessedFilters = {
  pageSize: 0,
  fileName: '',
  fileReceivedDate: new Date(),
  fileSavedDate: EmptyDateRange,
  duration: '',
  relativeFilePath: '',
  notesCount: O.none,
  fileAcceptanceStatus: FilterOptionValue.EmptyOption(),
  scheduleHistoryStatus: FilterOptionValue.EmptyOption(),
};

export type EnrollmentFilesNotProcessedFilters = {
  pageSize?: number;
  fileName: string;
  fileReceivedDate: Date;
  fileSavedDate: DateRange;
  duration: string;
  relativeFilePath: string;
  notesCount: InequalityRangeFilter;
  fileAcceptanceStatus: FilterOptionUnion;
  scheduleHistoryStatus: FilterOptionUnion;
};

export const getEnrollmentFilesNotProcessed = (
  pageOption?: Page<EnrollmentFilesNotProcessed>,
  filter?: EnrollmentFilesNotProcessedFilters
) => {
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );
  return createAction(EnrollmentFilesNotProcessedActionType.GET_ENROLLMENT_FILES_NOT_PROCESSED, payload);
};
export const changeEnrollmentFilesNotProcessed = (
  enrollmentFileNotProcessedReports: AsyncPage<Page<EnrollmentFilesNotProcessed>>
) =>
  createAction(
    EnrollmentFilesNotProcessedActionType.CHANGE_ENROLLMENT_FILES_NOT_PROCESSED,
    enrollmentFileNotProcessedReports
  );

export const changeFilesNotProcessedFilters = (filters: EnrollmentFilesNotProcessedFilters) =>
  createAction(EnrollmentFilesNotProcessedActionType.CHANGE_ENROLLMENT_FILES_NOT_PROCESSED_FILTER, {filters});

export const getFileAndScheduleHistoryStatus = (data: PendingEnrollmentFileStatus) =>
  createAction(EnrollmentFilesNotProcessedActionType.GET_ENROLLMENT_FILE_STATUS, {data});

export const changeFileAndScheduleHistoryStatus = (data: Async<EnrollmentFileStatus>) =>
  createAction(EnrollmentFilesNotProcessedActionType.CHANGE_ENROLLMENT_FILE_STATUS, data);

type EnrollmentFilesNotProcessedState = {
  enrollmentFilesNotProcessedPage: AsyncPage<Page<EnrollmentFilesNotProcessed>>;
  EnrollmentFilesNotProcessedFilters: EnrollmentFilesNotProcessedFilters;
  filter: EnrollmentFilesNotProcessedFilters;
  enrollmentFileStatus: Async<EnrollmentFileStatus>;
  PendingEnroll: PendingEnrollmentFileStatus;
};

export const DEFAULT_STATE: EnrollmentFilesNotProcessedState = {
  enrollmentFilesNotProcessedPage: new Empty(),
  EnrollmentFilesNotProcessedFilters: EnrollmentFileRejectFilters,
  filter: EnrollmentFileRejectFilters,
  enrollmentFileStatus: new Empty(),
  PendingEnroll: PendingEnroll,
};

type EnrollmentFilesNotprocessedAction =
  | ReturnType<typeof getEnrollmentFilesNotProcessed>
  | ReturnType<typeof changeEnrollmentFilesNotProcessed>
  | ReturnType<typeof changeFilesNotProcessedFilters>
  | ReturnType<typeof changeFileAndScheduleHistoryStatus>;

export default (
  state: EnrollmentFilesNotProcessedState = DEFAULT_STATE,
  action: EnrollmentFilesNotprocessedAction
): EnrollmentFilesNotProcessedState => {
  switch (action.type) {
    case EnrollmentFilesNotProcessedActionType.CHANGE_ENROLLMENT_FILES_NOT_PROCESSED:
      return {
        ...state,
        enrollmentFilesNotProcessedPage: action.payload,
      };
    case EnrollmentFilesNotProcessedActionType.CHANGE_ENROLLMENT_FILES_NOT_PROCESSED_FILTER:
      return {
        ...state,
        filter: action.payload.filters,
      };
    case EnrollmentFilesNotProcessedActionType.CHANGE_ENROLLMENT_FILE_STATUS:
      return {
        ...state,
        enrollmentFileStatus: action.payload,
      };
    default:
      return state;
  }
};
